import { useCallback, useEffect, useRef, useState } from 'react'
import Router, { useRouter } from 'next/router'

/**
 * A filter value mirrored in the URL query (?category=infrastructure), so
 * filtered views can be shared and linked. SSR-safe: the first render always
 * uses the fallback, and the URL is read once the router is ready.
 */
export const useQueryFilter = (key: string, allowed: Array<string>, fallback = 'all'): [string, (value: string) => void] => {
    const router = useRouter()
    const [value, setValue] = useState(fallback)
    const raw = router.query[key]
    const fromUrl = typeof raw === 'string' ? raw : null
    const allowedKey = allowed.join('|')

    useEffect(() => {
        if (!router.isReady) return

        const options = allowedKey.split('|')

        setValue(fromUrl && options.includes(fromUrl) ? fromUrl : fallback)
    }, [router.isReady, fromUrl, allowedKey, fallback])

    const update = useCallback((next: string) => {
        setValue(next)

        const query = { ...Router.query }

        if (next === fallback) delete query[key]
        else query[key] = next

        void Router.replace({ pathname: Router.pathname, query }, undefined, { shallow: true, scroll: false })
    }, [router, key, fallback])

    return [value, update]
}

/**
 * Free-text search mirrored in the URL (?q=...). The input updates
 * immediately; the URL follows after a short pause in typing.
 */
export const useQueryText = (key: string, delay = 300): [string, (value: string) => void] => {
    const router = useRouter()
    const [value, setValue] = useState('')
    const timer = useRef<number | null>(null)
    const raw = router.query[key]
    const initial = typeof raw === 'string' ? raw : ''

    useEffect(() => {
        if (router.isReady) setValue(initial)
        // Read the URL once when the page becomes ready, not on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady])

    useEffect(() => () => {
        if (timer.current) window.clearTimeout(timer.current)
    }, [])

    const update = useCallback((next: string) => {
        setValue(next)

        if (timer.current) window.clearTimeout(timer.current)

        timer.current = window.setTimeout(() => {
            const query = { ...Router.query }

            if (next.trim() === '') delete query[key]
            else query[key] = next

            void Router.replace({ pathname: Router.pathname, query }, undefined, { shallow: true, scroll: false })
        }, delay)
    }, [router, key, delay])

    return [value, update]
}
